import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Card } from '@reflex/ui/components/ui/card';
import { Collapsible, CollapsibleContent } from '@reflex/ui/components/ui/collapsible';
import { InlineDocsHint, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { IntegrationIcon } from './StatusBadge';
import { SecretRow } from './SecretRow';
import type { IntegrationSecretStatus, SecretStatusItem } from '@/lib/types';

function SecretsSummary({ secrets }: { secrets: SecretStatusItem[] }) {
  if (secrets.length === 0) return null;
  const setCount = secrets.filter((s) => s.isSet).length;
  return (
    <span className="text-xs text-muted-foreground">
      {setCount}/{secrets.length} configured
    </span>
  );
}

/**
 * Stops a child interactive element (the docs hint button, an `<a>` inside
 * the description, etc.) from also toggling the surrounding collapsible
 * header. The header is a `role="button"` div rather than a real `<button>`
 * — that lets us nest the docs-hint button between the title and the
 * status badge without producing invalid button-in-button markup or
 * forwarding the docs click to the collapse toggle.
 */
function stopHeaderActivation(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void {
  event.stopPropagation();
}

export function IntegrationSecretGroup({
  integration,
  onEditSecret,
  isPending,
  defaultOpen,
  docsSlug,
  description,
  renderBeforeEditAction,
  getAddLabel,
  footer,
}: {
  integration: IntegrationSecretStatus;
  onEditSecret: (item: SecretStatusItem) => void;
  isPending?: boolean;
  defaultOpen?: boolean;
  /**
   * Optional docs slug (with optional `#anchor`) for an inline hint next to
   * the integration title. The hint is non-interactive when the slug is
   * absent, so admin surfaces that don't have a matching doc keep their
   * existing layout.
   */
  docsSlug?: string;
  /**
   * Optional one-line summary rendered under the integration title. The
   * org-setup wizard passes a category-specific blurb (sandbox vs. LLM
   * providers) so admins see what the group is for before they decide to
   * dive into the docs; admin surfaces that already explain the group
   * elsewhere can leave this unset.
   *
   * Accepts `ReactNode` so callers can interleave links / emphasis without
   * losing the underlying layout. The wizard surfaces the Runloop paid-tier
   * requirement here (trial keys can't provision XL devboxes) alongside a
   * link to the platform settings page where keys are created.
   */
  description?: ReactNode;
  /** Optional per-secret action placed after Get key and before Add/Edit. */
  renderBeforeEditAction?: (item: SecretStatusItem) => ReactNode;
  /** Optional per-secret replacement label for an unset secret's Add action. */
  getAddLabel?: (item: SecretStatusItem) => string | undefined;
  /** Optional content shown after the last secret row. */
  footer?: ReactNode;
}) {
  // Controlled `Collapsible` state so the header can be a `role="button"`
  // div rather than a `<CollapsibleTrigger>` button. Wrapping the entire
  // row in a real button would force the docs-hint button to be a sibling
  // outside the row (its previous far-right position), or nested inside —
  // which is invalid HTML and breaks keyboard semantics. The div + manual
  // a11y attributes let the docs hint sit between the title and the
  // status badge while keeping Enter/Space toggling.
  const [open, setOpen] = useState(defaultOpen ?? false);

  function toggle(): void {
    setOpen((prev) => !prev);
  }

  function handleHeaderKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  }

  // Model providers don't mark any single key as required — an org just
  // needs at least one so chat and agents have a model to call. When the
  // group is otherwise "ready" (nothing required is missing) but the user
  // hasn't set a single key, "Ready" reads as misleading. Surface a soft
  // nudge instead so they know they should pick at least one, without
  // hard-blocking them the way a missing *required* key would.
  const setCount = integration.secrets.filter((s) => s.isSet).length;
  const recommendsAtLeastOne = integration.category === 'models';
  const showRecommendNudge = integration.ready && recommendsAtLeastOne && setCount === 0;

  if (integration.secrets.length === 0) {
    return (
      <Card className="gap-0 p-4">
        <div className="flex items-center gap-3">
          <IntegrationIcon icon={integration.icon} />
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <PanelTitle as="h3" size="sm">
                {integration.name}
              </PanelTitle>
              {docsSlug && <InlineDocsHint slug={docsSlug} />}
              <Badge variant="secondary" className="text-xs font-normal">
                OAuth
              </Badge>
            </div>
            {description && (
              <p className="text-xs leading-snug text-muted-foreground/80">{description}</p>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      {/* `gap-0 p-0 overflow-hidden` neutralises the default `Card` padding
          + flex gap so the inner header owns its own `p-4` and the
          collapsible body can extend flush to the rounded card edges. The
          previous layout left the SecretRow inside `Card { p-4 }`, which
          stacked the row's own `py-3` on top of the card's 16px bottom
          padding and produced the lopsided "extra air" below the last
          row that this card was reported for. */}
      <Card
        id={`integration-secret-group-${integration.id}`}
        data-testid={`integration-secret-group-${integration.id}`}
        className="gap-0 overflow-hidden p-0"
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          data-state={open ? 'open' : 'closed'}
          onClick={toggle}
          onKeyDown={handleHeaderKeyDown}
          className="group flex min-w-0 cursor-pointer flex-col gap-1 p-4 text-left transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        >
          <div className="flex w-full min-w-0 items-center gap-3">
            <IntegrationIcon icon={integration.icon} />
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <PanelTitle as="span" size="sm" className="min-w-0 text-left">
                {integration.name}
              </PanelTitle>
              {docsSlug && (
                <span
                  className="inline-flex shrink-0"
                  onClick={stopHeaderActivation}
                  onKeyDown={stopHeaderActivation}
                >
                  <InlineDocsHint slug={docsSlug} />
                </span>
              )}
              {showRecommendNudge ? (
                <Badge
                  variant="secondary"
                  className="gap-1 text-xs font-normal text-status-accent-orange"
                >
                  Set at least one
                </Badge>
              ) : integration.ready ? (
                <Badge
                  variant="secondary"
                  className="gap-1 text-xs font-normal text-status-success"
                >
                  <Check className="h-3 w-3" /> Ready
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="gap-1 text-xs font-normal text-status-accent-orange"
                >
                  {integration.missingRequired.length} missing
                </Badge>
              )}
            </div>
            <SecretsSummary secrets={integration.secrets} />
            {/* `ChevronDown` rotated `-90deg` when closed reads more
                naturally as an accordion affordance than `ChevronRight`
                rotated 90deg when open — the at-rest state is the
                familiar "drill down" arrow rather than a right-tick. */}
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
          </div>
          {description && (
            // `ml-11` aligns the secondary line with the title text:
            // 32px (icon) + 12px (gap-3) = 44px = ml-11. The prior
            // `ml-9` shifted it 8px left of the title.
            <div
              className="ml-11 text-xs leading-snug text-muted-foreground/80"
              onClick={stopHeaderActivation}
              onKeyDown={stopHeaderActivation}
            >
              {description}
            </div>
          )}
        </div>
        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          {/* Subtle `bg-muted/20` separates the expanded body from the
              clickable header without adding another border line. Rows
              keep their own `px-4` so the title column stays aligned
              with the header above. */}
          <div className="divide-y divide-border/60 border-t border-border/60 bg-muted/20">
            {integration.secrets.map((item) => (
              <SecretRow
                key={item.key}
                item={item}
                onEdit={() => onEditSecret(item)}
                isPending={isPending}
                beforeEditAction={renderBeforeEditAction?.(item)}
                addLabel={getAddLabel?.(item)}
              />
            ))}
            {footer ? <div className="p-4">{footer}</div> : null}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
